import { useState } from 'react'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,

  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material'
import {
  TableChart as ExcelIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material'
import ReportsTabs from '../../components/reports/ReportsTabs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format, subDays } from 'date-fns'
import { useSalesTaxReport, useGenerateSalesTaxExcel } from '../../hooks/api/useReports'
import { formatCurrencyWithLocale as formatCurrency } from '../../utils/formatters'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { RootState } from '../../store'

const SalesTaxReportPage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const salesTaxEnabled = user?.company_features?.sales_tax_invoice === true
  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 30))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [viewReport, setViewReport] = useState(false)

  const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : ''
  const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : ''

  const { data: reportData, isLoading } = useSalesTaxReport(
    salesTaxEnabled && viewReport && formattedStartDate && formattedEndDate
      ? { startDate: formattedStartDate, endDate: formattedEndDate, format: 'json' }
      : undefined
  )

  const generateExcelMutation = useGenerateSalesTaxExcel()
  const isGenerating = generateExcelMutation.isPending

  const reportTotals = reportData?.data.reduce(
    (totals, row) => ({
      valueExcludingSalesTax: totals.valueExcludingSalesTax + row.value_excluding_sales_tax,
      salesTaxAmount: totals.salesTaxAmount + row.sales_tax_amount,
      quantityCtn: totals.quantityCtn + row.quantity_ctn,
    }),
    { valueExcludingSalesTax: 0, salesTaxAmount: 0, quantityCtn: 0 }
  )

  const handleViewReport = () => {
    if (startDate && endDate) setViewReport(true)
  }

  const handleGenerateExcel = () => {
    if (formattedStartDate && formattedEndDate) {
      generateExcelMutation.mutate({ startDate: formattedStartDate, endDate: formattedEndDate })
    }
  }

  if (!salesTaxEnabled) return <Navigate to={user?.company_features?.advance_tax !== false ? '/reports' : '/reports/pnl'} replace />

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h4" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReportIcon /> Reports
        </Typography>

        <ReportsTabs activeTab={1} />

        {/* Date Range Selector */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sales Tax Report
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            Generate sales tax reports for Sales Tax Invoice (STI) customers (18%)
          </Typography>

          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(v) => { setStartDate(v); setViewReport(false) }}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(v) => { setEndDate(v); setViewReport(false) }}
                minDate={startDate || undefined}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={handleViewReport}
                  disabled={!startDate || !endDate || isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} /> : <ReportIcon />}
                >
                  View Report
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleGenerateExcel}
                  disabled={!startDate || !endDate || isGenerating}
                  startIcon={isGenerating ? <CircularProgress size={20} /> : <ExcelIcon />}
                >
                  Generate Excel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading report data...</Typography>
          </Box>
        )}

        {viewReport && reportData && (
          <Box>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Total Customers
                    </Typography>
                    <Typography variant="h4">
                      {reportData.summary.total_customers.toLocaleString()}
                    </Typography>
                    <StatusChip label="STI @ 18%" variant="info" />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Total Bills
                    </Typography>
                    <Typography variant="h4">
                      {reportData.summary.total_bills.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Value Excluding Sales Tax
                    </Typography>
                    <Typography variant="h5">
                      {formatCurrency(reportTotals?.valueExcludingSalesTax)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Total Sales Tax (18%)
                    </Typography>
                    <Typography variant="h5" color="error">
                      {formatCurrency(reportTotals?.salesTaxAmount)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Data Table */}
            {reportData.data.length > 0 ? (
              <Paper>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f7f5f2' }}>
                        <TableCell>#</TableCell>
                        <TableCell>NTN</TableCell>
                        <TableCell>Customer Name</TableCell>
                        <TableCell>Invoice No</TableCell>
                        <TableCell>Invoice Date</TableCell>
                        <TableCell align="right">Value Excluding Sales Tax</TableCell>
                        <TableCell align="right">Sales Tax Amount</TableCell>
                        <TableCell align="right">Qty (Ctns)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.data.map((row) => (
                        <TableRow key={`${row.invoice_number}-${row.serial_number}`} hover>
                          <TableCell>{row.serial_number}</TableCell>
                          <TableCell>{row.taxpayer_ntn || '—'}</TableCell>
                          <TableCell>{row.taxpayer_name}</TableCell>
                          <TableCell>{row.invoice_number || '—'}</TableCell>
                          <TableCell>{format(new Date(row.invoice_date), 'dd/MM/yyyy')}</TableCell>
                          <TableCell align="right">{formatCurrency(row.value_excluding_sales_tax)}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>
                            {formatCurrency(row.sales_tax_amount)}
                          </TableCell>
                          <TableCell align="right">{row.quantity_ctn.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: '#f7f5f2' }}>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" fontWeight="bold">Total</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(reportTotals?.valueExcludingSalesTax)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="error">
                            {formatCurrency(reportTotals?.salesTaxAmount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {reportTotals?.quantityCtn.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No sales tax invoices found for the selected period.
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default SalesTaxReportPage
